import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
import EXIF from 'exif-js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './Gallery.css';
import { getApiUrl } from '../config';

function Gallery({ username, onLogout }) {
  const [photos, setPhotos] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [useTakenDate, setUseTakenDate] = useState(false);
  const [gridColumns, setGridColumns] = useState(() => {
    const saved = localStorage.getItem('gridColumns');
    return saved ? parseInt(saved) : 3;
  });
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [likes, setLikes] = useState([]);
  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [showLikes, setShowLikes] = useState(false);
  const [photoDates, setPhotoDates] = useState({});

  useEffect(() => {
    fetchPhotos();
    fetchPhotoDates();
    setDeleteMode(false);
    setSelectedPhotoIds([]);
  }, [selectedDate]);

  useEffect(() => {
    localStorage.setItem('gridColumns', gridColumns);
  }, [gridColumns]);

  const fetchPhotos = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await fetch(getApiUrl(`/api/photos?date=${dateStr}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Convert relative photo URLs to absolute URLs
        const photosWithAbsoluteUrls = (data.photos || []).map(photo => ({
          ...photo,
          url: getApiUrl(photo.url),
          thumbnailUrl: getApiUrl(photo.thumbnailUrl)
        }));
        setPhotos(photosWithAbsoluteUrls);
      }
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    }
  };

  const fetchPhotoDates = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/photos/dates'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPhotoDates(data.dates || {});
      }
    } catch (err) {
      console.error('Failed to fetch photo dates:', err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(file =>
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );
    setSelectedFiles(files);
  };

  const getExifDate = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          EXIF.getData(img, function() {
            const dateTime = EXIF.getTag(this, 'DateTimeOriginal') || EXIF.getTag(this, 'DateTime');
            if (dateTime) {
              // EXIF 날짜 형식: "2024:05:26 14:30:00"
              const [datePart] = dateTime.split(' ');
              const formattedDate = datePart.replace(/:/g, '-');
              resolve(formattedDate);
            } else {
              resolve(null);
            }
          });
        };
        img.onerror = () => resolve(null);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  const generateVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      video.onloadedmetadata = () => {
        video.currentTime = 1; // 1초 지점의 프레임 추출
      };

      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.8);
      };

      video.onerror = () => {
        console.error('Failed to load video for thumbnail generation');
        resolve(null);
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });

    try {
      const token = localStorage.getItem('authToken');
      let successCount = 0;

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadProgress({ current: i + 1, total: selectedFiles.length });

        // 촬영 날짜 사용 옵션이 활성화된 경우 EXIF 날짜 추출
        let uploadDate = format(selectedDate, 'yyyy-MM-dd');
        if (useTakenDate) {
          const exifDate = await getExifDate(file);
          console.log(`File: ${file.name}, EXIF Date: ${exifDate}`);
          if (exifDate) {
            uploadDate = exifDate;
          } else {
            console.warn(`No EXIF date found for ${file.name}, using selected date`);
          }
        }
        console.log(`Uploading ${file.name} with date: ${uploadDate}`);

        const formData = new FormData();
        formData.append('photo', file);
        formData.append('date', uploadDate);

        // 동영상이면 썸네일 생성
        if (file.type.startsWith('video/')) {
          const thumbnail = await generateVideoThumbnail(file);
          if (thumbnail) {
            formData.append('thumbnail', thumbnail, 'thumbnail.jpg');
          }
        }

        try {
          const response = await fetch(getApiUrl('/api/photos/upload'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          if (response.ok) {
            successCount++;
          }
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
        }
      }

      setShowUploadModal(false);
      setSelectedFiles([]);
      setUploadProgress({ current: 0, total: 0 });
      fetchPhotos();

      if (successCount < selectedFiles.length) {
        alert(`${successCount}/${selectedFiles.length}개 사진이 업로드되었습니다.`);
      }
    } catch (err) {
      alert('업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('이 사진을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/photos/delete/${photoId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchPhotos();
      } else {
        const data = await response.json();
        alert(data.error || '삭제에 실패했습니다.');
      }
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotoIds(prev => {
      if (prev.includes(photoId)) {
        return prev.filter(id => id !== photoId);
      } else {
        return [...prev, photoId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedPhotoIds.length === 0) {
      alert('삭제할 사진을 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedPhotoIds.length}개의 사진을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      let successCount = 0;

      for (const photoId of selectedPhotoIds) {
        try {
          const response = await fetch(getApiUrl(`/api/photos/delete/${photoId}`), {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            successCount++;
          }
        } catch (err) {
          console.error(`Failed to delete photo ${photoId}:`, err);
        }
      }

      setSelectedPhotoIds([]);
      setDeleteMode(false);
      fetchPhotos();

      if (successCount < selectedPhotoIds.length) {
        alert(`${successCount}/${selectedPhotoIds.length}개 사진이 삭제되었습니다.`);
      }
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteExceptLiked = async () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    if (!window.confirm(`${dateStr}의 좋아요가 없는 파일들을 모두 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/photos/delete-except-liked'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date: dateStr })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`${data.deleted}개의 파일이 삭제되었습니다.`);
        fetchPhotos();
        fetchPhotoDates();
      } else {
        const data = await response.json();
        alert(data.error || '삭제에 실패했습니다.');
      }
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const toggleSelectAll = () => {
    if (selectedPhotoIds.length === photos.length) {
      setSelectedPhotoIds([]);
    } else {
      setSelectedPhotoIds(photos.map(p => p.id));
    }
  };

  const formatDateKorean = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'yyyy년 M월 d일 (eee)', { locale: ko });
  };

  const fetchComments = async (photoId) => {
    setLoadingComments(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/photos/${photoId}/comments`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async (photoId) => {
    if (!newComment.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/photos/${photoId}/comments`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comment: newComment.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setComments([...comments, data.comment]);
        setNewComment('');
      } else {
        const data = await response.json();
        alert(data.error || '댓글 작성에 실패했습니다.');
      }
    } catch (err) {
      alert('댓글 작성 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/comments/${commentId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setComments(comments.filter(c => c.id !== commentId));
      } else {
        const data = await response.json();
        alert(data.error || '댓글 삭제에 실패했습니다.');
      }
    } catch (err) {
      alert('댓글 삭제 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    if (viewingPhoto) {
      fetchComments(viewingPhoto.id);
      fetchLikes(viewingPhoto.id);
      setNewComment('');
      setShowComments(false);
      setShowLikes(false);
    } else {
      setComments([]);
      setLikes([]);
      setLikeCount(0);
      setIsLiked(false);
      setShowComments(false);
      setShowLikes(false);
    }
  }, [viewingPhoto]);

  const fetchLikes = async (photoId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/photos/${photoId}/likes`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLikes(data.likes || []);
        setLikeCount(data.likes?.length || 0);
        setIsLiked(data.likes?.some(like => like.username === username) || false);
      }
    } catch (err) {
      console.error('Failed to fetch likes:', err);
    }
  };

  const handleToggleLike = async (photoId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/photos/${photoId}/likes`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeCount(data.count);
        // Refresh likes list
        fetchLikes(photoId);
        // Refresh photo list to update like badges
        fetchPhotos();
      }
    } catch (err) {
      alert('좋아요 처리 중 오류가 발생했습니다.');
    }
  };

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    const currentIndex = photos.findIndex(p => p.id === viewingPhoto.id);

    if (isLeftSwipe && currentIndex < photos.length - 1) {
      // 왼쪽으로 스와이프 = 다음 사진
      setViewingPhoto(photos[currentIndex + 1]);
    }

    if (isRightSwipe && currentIndex > 0) {
      // 오른쪽으로 스와이프 = 이전 사진
      setViewingPhoto(photos[currentIndex - 1]);
    }
  };

  return (
    <div className="gallery-container">
      <header className="gallery-header">
        <div className="header-content">
          <h1>💕 PhotoTogether</h1>
          <div className="user-info">
            <span className="username">{username}님</span>
            <button onClick={onLogout} className="logout-button">로그아웃</button>
          </div>
        </div>
      </header>

      <div className="gallery-content">
        <div className="controls-container">
          <div className="date-selector">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="yyyy-MM-dd"
              locale={ko}
              className="date-input"
              dayClassName={(date) => {
                const dateStr = format(date, 'yyyy-MM-dd');
                return photoDates[dateStr] ? 'has-photos' : undefined;
              }}
              calendarClassName="custom-calendar"
            />
            <h2 className="selected-date">{formatDateKorean(format(selectedDate, 'yyyy-MM-dd'))}</h2>
          </div>

          <div className="right-controls">
            <select
              id="grid-columns-select"
              value={gridColumns}
              onChange={(e) => setGridColumns(parseInt(e.target.value))}
              className="grid-select"
            >
              {[3, 4, 5, 6, 7, 8, 9, 10].map((cols) => (
                <option key={cols} value={cols}>
                  {cols}열
                </option>
              ))}
            </select>

            {photos.length > 0 && !deleteMode && (
              <>
                <button
                  onClick={handleDeleteExceptLiked}
                  className="delete-except-liked-button"
                  title="좋아요가 없는 사진 삭제"
                >
                  ♡ 제외 삭제
                </button>
                <button
                  onClick={() => setDeleteMode(true)}
                  className="bulk-delete-button"
                >
                  일괄 삭제
                </button>
              </>
            )}
          </div>
        </div>

        {deleteMode && photos.length > 0 && (
          <div className="bulk-actions-bar">
            <button
              onClick={toggleSelectAll}
              className="select-all-button"
            >
              {selectedPhotoIds.length === photos.length ? '전체 해제' : '전체 선택'}
            </button>
            <span className="selected-count">
              {selectedPhotoIds.length}개 선택됨
            </span>
            <button
              onClick={handleBulkDelete}
              className="confirm-delete-button"
              disabled={selectedPhotoIds.length === 0}
            >
              삭제
            </button>
            <button
              onClick={() => {
                setDeleteMode(false);
                setSelectedPhotoIds([]);
              }}
              className="cancel-delete-button"
            >
              취소
            </button>
          </div>
        )}

        <button
          onClick={() => setShowUploadModal(true)}
          className="upload-fab"
          title="사진 업로드"
        >
          📷
        </button>

        <div
          className="photos-grid"
          style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}
        >
          {photos.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📸</div>
              <p>아직 업로드된 사진이 없습니다</p>
              <p className="empty-hint">첫 번째 사진을 업로드해보세요!</p>
            </div>
          ) : (
            photos.map((photo) => (
              <div
                key={photo.id}
                className={`photo-card ${deleteMode ? 'delete-mode' : ''} ${selectedPhotoIds.includes(photo.id) ? 'selected' : ''}`}
              >
                <div
                  className="photo-image-area"
                  onClick={() => deleteMode ? togglePhotoSelection(photo.id) : setViewingPhoto(photo)}
                >
                  <img src={photo.thumbnailUrl} alt={photo.caption || ''} />
                  {photo.is_video && !deleteMode && (
                    <div className="video-play-icon">▶</div>
                  )}
                  {photo.like_count > 0 && !deleteMode && (
                    <div className="photo-like-badge">
                      ❤️
                    </div>
                  )}
                  {deleteMode && (
                    <div className="checkbox-overlay">
                      <input
                        type="checkbox"
                        checked={selectedPhotoIds.includes(photo.id)}
                        onChange={() => togglePhotoSelection(photo.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="photo-checkbox"
                      />
                    </div>
                  )}
                </div>
                {!deleteMode && (
                  <button
                    className="delete-photo-button"
                    onClick={() => handleDeletePhoto(photo.id)}
                    title="삭제"
                  >
                    ✕
                  </button>
                )}
                <div className="photo-info">
                  <span className="photo-uploader">{photo.username}</span>
                  <span className="photo-time">
                    {format(new Date(photo.uploaded_at), 'HH:mm')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showUploadModal && (
        <div className="modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>사진 업로드</h3>

            <div className="upload-options">
              <label className="date-option">
                <input
                  type="radio"
                  name="dateOption"
                  checked={!useTakenDate}
                  onChange={() => setUseTakenDate(false)}
                />
                <span>선택한 날짜로 업로드</span>
              </label>
              <label className="date-option">
                <input
                  type="radio"
                  name="dateOption"
                  checked={useTakenDate}
                  onChange={() => setUseTakenDate(true)}
                />
                <span>촬영 날짜로 업로드 (EXIF)</span>
              </label>
            </div>

            {selectedFiles.length === 0 ? (
              <label className="file-upload-area">
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <div className="upload-icon">📁</div>
                <p>클릭하여 사진/동영상 선택</p>
                <p className="upload-hint">여러 개 선택 가능</p>
              </label>
            ) : (
              <div className="preview-area">
                <div className="file-count">
                  {selectedFiles.length}개의 파일 선택됨
                </div>
                <div className="file-list">
                  {selectedFiles.slice(0, 5).map((file, index) => (
                    <div key={index} className="file-item">
                      {file.type.startsWith('video/') ? (
                        <video
                          src={URL.createObjectURL(file)}
                          className="file-thumbnail"
                          muted
                        />
                      ) : (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="file-thumbnail"
                        />
                      )}
                    </div>
                  ))}
                  {selectedFiles.length > 5 && (
                    <div className="file-item more-files">
                      +{selectedFiles.length - 5}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setSelectedFiles([])}
                  className="change-photo-button"
                  disabled={uploading}
                >
                  다시 선택
                </button>
              </div>
            )}

            {uploading && uploadProgress.total > 0 && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
                <p className="progress-text">
                  {uploadProgress.current} / {uploadProgress.total} 업로드 중...
                </p>
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={() => setShowUploadModal(false)}
                className="cancel-button"
                disabled={uploading}
              >
                취소
              </button>
              <button
                onClick={handleUpload}
                className="upload-button"
                disabled={selectedFiles.length === 0 || uploading}
              >
                {uploading ? '업로드 중...' : `${selectedFiles.length}개 업로드`}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingPhoto && (
        <div className="photo-viewer-overlay" onClick={() => setViewingPhoto(null)}>
          <div className="photo-viewer-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="close-viewer-button"
              onClick={() => setViewingPhoto(null)}
              title="닫기"
            >
              ✕
            </button>

            {photos.findIndex(p => p.id === viewingPhoto.id) > 0 && (
              <button
                className="nav-button nav-left"
                onClick={() => {
                  const currentIndex = photos.findIndex(p => p.id === viewingPhoto.id);
                  setViewingPhoto(photos[currentIndex - 1]);
                }}
                title="이전 사진"
              >
                ◀
              </button>
            )}

            {photos.findIndex(p => p.id === viewingPhoto.id) < photos.length - 1 && (
              <button
                className="nav-button nav-right"
                onClick={() => {
                  const currentIndex = photos.findIndex(p => p.id === viewingPhoto.id);
                  setViewingPhoto(photos[currentIndex + 1]);
                }}
                title="다음 사진"
              >
                ▶
              </button>
            )}

            {viewingPhoto.is_video ? (
              <video
                src={viewingPhoto.url}
                className="viewer-image"
                controls
                autoPlay
                loop
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              />
            ) : (
              <img
                src={viewingPhoto.url}
                alt=""
                className="viewer-image"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              />
            )}
            <div className="viewer-info">
              <span className="viewer-uploader">{viewingPhoto.username}</span>
              <span className="viewer-time">
                {format(new Date(viewingPhoto.uploaded_at), 'yyyy년 M월 d일 HH:mm', { locale: ko })}
              </span>
              <div className="viewer-actions">
                <div className="like-button-container">
                  <button
                    className="like-heart-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleLike(viewingPhoto.id);
                    }}
                    title={isLiked ? '좋아요 취소' : '좋아요'}
                  >
                    {isLiked ? '❤️' : '🤍'}
                  </button>
                  <div
                    className="like-count-area"
                    onClick={() => {
                      setShowLikes(!showLikes);
                      if (!showLikes) setShowComments(false);
                    }}
                  >
                    {likeCount}
                  </div>
                </div>
                <button
                  className="comments-toggle-button"
                  onClick={() => {
                    setShowComments(!showComments);
                    if (!showComments) setShowLikes(false);
                  }}
                >
                  💬 댓글 {comments.length}
                </button>
              </div>
            </div>

            {showLikes && (
              <div className="likes-popup">
                <div className="likes-popup-header">
                  <h4 className="likes-title">좋아요 {likeCount}</h4>
                  <button
                    className="likes-close-button"
                    onClick={() => setShowLikes(false)}
                  >
                    ✕
                  </button>
                </div>

                <div className="likes-list">
                  {likes.length === 0 ? (
                    <div className="no-likes">아직 좋아요가 없습니다</div>
                  ) : (
                    likes.map((like) => (
                      <div key={like.id} className="like-item">
                        <span className="like-username">❤️ {like.username}</span>
                        <span className="like-time">
                          {format(new Date(like.created_at), 'M/d HH:mm')}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {showComments && (
              <div className="comments-popup">
                <div className="comments-popup-header">
                  <h4 className="comments-title">댓글 {comments.length}</h4>
                  <button
                    className="comments-close-button"
                    onClick={() => setShowComments(false)}
                  >
                    ✕
                  </button>
                </div>

                <div className="comments-list">
                  {loadingComments ? (
                    <div className="comments-loading">댓글 로딩 중...</div>
                  ) : comments.length === 0 ? (
                    <div className="no-comments">첫 댓글을 남겨보세요!</div>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="comment-item">
                        <div className="comment-header">
                          <span className="comment-author">{comment.username}</span>
                          <span className="comment-time">
                            {format(new Date(comment.created_at), 'M/d HH:mm')}
                          </span>
                          {comment.username === username && (
                            <button
                              className="comment-delete-button"
                              onClick={() => handleDeleteComment(comment.id)}
                              title="삭제"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div className="comment-text">{comment.comment_text}</div>
                      </div>
                    ))
                  )}
                </div>

                <div className="comment-input-area">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment(viewingPhoto.id);
                      }
                    }}
                    placeholder="댓글을 입력하세요..."
                    className="comment-input"
                  />
                  <button
                    onClick={() => handleAddComment(viewingPhoto.id)}
                    className="comment-submit-button"
                    disabled={!newComment.trim()}
                  >
                    작성
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;
